import { locationRoute } from "@/App";
import { MenuItem } from "@/hooks/useMenu";
import { useTrackingEvent } from "@/hooks/useTrackingEvent";
import { cn, formatPriceToDkk, getImageUrl } from "@/lib/utils";
import { MenuContext } from "@/routes/Menu";
import { CartItem, useCartStore } from "@/store/CartStore";
import { useContext } from "react";
import { Button } from "./ui/button";
import { XIcon } from "lucide-react";

export function UpsaleItem({
  item,
  cartItem,
}: {
  item: MenuItem;
  cartItem?: CartItem;
}) {
  const { setUnavailableProductOpen, setUnavailableProductPausedUntil, handleOpenProduct, handleEditCartItem } =
    useContext(MenuContext);
  const { locationId } = locationRoute.useParams();

  const { cart, add, setCount } = useCartStore();

  const tracking = useTrackingEvent();

  const variant = item.options.length > 1 ? undefined : item.options[0];

  const quantity = 1;

  function handleAddToCart() {
    if (!variant) return;

    // Add the item with both addons and condiments.
    add(
      {
        ...variant,
        isUpsale: true,
        addons: [],
        cartCondiment: [],
      },
      quantity
    );
    try {
      tracking.capture("upsale_added_to_cart", {
        locationId: locationId,
        product_name: item.name,
        variant_name: variant.name,
        quantity,
      });
    } catch (error) {
      console.error("Error capturing upsale_added_to_cart event:", error);
    }
  }

  function removeFromCart() {
    if (!cartItem) return;
    setCount(cartItem.lineId, 0);
  }

  if (item === undefined) return;

  const requiredAddons =
    item.addonGroups !== undefined
      ? item.addonGroups
          ?.map((group) => group.items?.map((addon) => addon))
          .flat()
          .filter((addon) => addon.min > 0)
      : [];
  const requiredAddonCost =
    requiredAddons.reduce(
      (total, addon) => total + addon.price * addon.min,
      0
    ) ?? 0;

  const defaultOrCheapestOption =
    item.options.find((option) => option.default) ?? item.options[0];

  const priceToUse = cartItem
    ? cartItem.price * cartItem.count +
      (cartItem?.addons !== undefined
        ? cartItem?.addons?.reduce(
            (total, addon) =>
              total + addon.price * addon.count * cartItem.count,
            0
          )
        : 0)
    : defaultOrCheapestOption?.price + requiredAddonCost;

  const quantityInCart = cartItem
    ? cartItem.count
    : cart.reduce(
        (acc, itemInCart) =>
          itemInCart.productId === item.id ? acc + itemInCart.count : acc,
        0
      );

  const isInCart = quantityInCart > 0;

  const hasImage = item.image && item.image !== "/images/irondice.jpeg";

  return (
    <div className="gap-2 justify-between items-center grid grid-cols-8">
      <button
        key={item.id}
        className={cn(
          "relative py-2 px-3 transition text-left first:pt-2  md:hover:bg-muted/50 h-full flex flex-col col-span-5"
        )}
        onClick={(_) => {
          if (item?.unavailable ?? false) {
            setUnavailableProductPausedUntil(item.paused_until ?? null);
            setUnavailableProductOpen(true);
            return;
          }

          cartItem && handleEditCartItem
            ? handleEditCartItem(cartItem)
            : handleOpenProduct(item.id, true);
        }}
      >
        <div
          className={cn("flex flex-row py-2 gap-x-2 w-full", {
            "opacity-40": item?.unavailable ?? false,
          })}
        >
          {hasImage && (
            <img
              src={getImageUrl(locationId, item.image)}
              alt={""}
              loading="lazy"
              width="48"
              height="48"
              className="aspect-square object-cover  bg-gray-300 size-8 rounded-md"
            />
          )}
          <div className="">
            <h3 className="text-sm font-sans tracking-tight text-foreground flex items-center gap-x-2 -mt-1 pr-4 relative w-max max-w-full">
              <span>{item.name}</span>

              {isInCart && (
                <div className="absolute top-0 -right-1 font-normal size-3 text-[10px] p-2 rounded-full bg-primary text-primary-foreground flex items-center justify-center z-[2]">
                  <span>{quantityInCart}</span>
                </div>
              )}
            </h3>

            {item.description && (
              <p className="text-[10px] text-muted-foreground whitespace-pre-line line-clamp-2">
                {item.description}
              </p>
            )}
          </div>
        </div>
      </button>

      <div className="col-span-3 pr-3 flex flex-col gap-y-2">
        {variant ? (
          <div className="flex gap-1 justify-end">
            {cartItem ? (
              <button
                className="border text-sm py-0.5 px-3 whitespace-nowrap"
                onClick={removeFromCart}
              >
                <XIcon className="h-4 w-4" />
              </button>
            ) : (
              <Button
                className="border text-sm py-0.5 px-3 whitespace-nowrap"
                onClick={handleAddToCart}
              >
                + {formatPriceToDkk(priceToUse ?? 0.0)}
              </Button>
            )}
          </div>
        ) : (
          <Button
            className="border text-xs py-0.5 px-3 whitespace-nowrap w-max ml-auto"
            onClick={(_) => {
              if (item?.unavailable ?? false) {
                setUnavailableProductOpen(true);
                return;
              }

              cartItem && handleEditCartItem
                ? handleEditCartItem(cartItem)
                : handleOpenProduct(item.id, true);
            }}
            variant={"outline"}
          >
            from {formatPriceToDkk(priceToUse ?? 0.0)}
          </Button>
        )}
      </div>
    </div>
  );
}
